import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Spinner, Empty, RequireTenant } from "../ui";
import { AjutorButton } from "../AjutorButton";
import { useAuth } from "../AuthContext";
import { Globe, UploadCloud, ExternalLink, Save, Rocket, EyeOff, Sparkles, ImagePlus, Plus, Trash2, PencilRuler, Loader2, Eye, X, GripVertical, ChevronUp, ChevronDown, Film, FileText, Video, History, RotateCcw, Clock } from "lucide-react";
import { toast } from "sonner";
import { BlockRenderer, LightboxProvider } from "./PublicSite";
import { setLeaveGuard, clearLeaveGuard } from "../unsavedGuard";

const BASE = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BASE}${u}`);

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/landing/upload-image", fd, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
  });
  return data.url;
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/landing/upload-file", fd, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
  });
  return data; // { url, kind, name }
}

function ImageUpload({ value, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Poza incarcata");
    } catch (err) { toast.error(apiError(err, "Incarcare esuata")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-16 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
        {value ? <img src={imgSrc(value)} alt="" className="w-full h-full object-cover" data-testid="img-preview" />
          : <ImagePlus className="w-5 h-5 text-slate-300" />}
      </div>
      <div className="flex flex-col gap-1">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1"
          data-testid="upload-image-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
          {value ? "Schimba poza" : "Incarca poza"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")}
            className="text-xs text-slate-400 hover:text-red-500 text-left" data-testid="remove-image-btn">
            Elimina
          </button>
        )}
      </div>
    </div>
  );
}

function MultiImageUpload({ values, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const list = values || [];
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 20 - list.length;
    if (remaining <= 0) { toast.error("Maxim 20 de poze"); if (ref.current) ref.current.value = ""; return; }
    const chosen = files.slice(0, remaining);
    setBusy(true);
    try {
      const urls = [];
      for (const f of chosen) urls.push(await uploadImage(f));
      onChange([...list, ...urls]);
      toast.success(`${urls.length} poze incarcate`);
      if (files.length > remaining) toast.error("Poti avea maxim 20 de poze in galerie");
    } catch (err) { toast.error(apiError(err, "Incarcare esuata")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const removeAt = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="space-y-3" data-testid="multi-image-upload">
      {list.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {list.map((u, i) => (
            <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`gallery-thumb-${i}`}>
              <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeAt(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`gallery-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1"
        data-testid="gallery-add-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
        Adauga poze
      </button>
    </div>
  );
}

const LABELS = {
  hero: "Hero", stats: "Statistici", services: "Servicii", cards: "Solutii / Produse",
  steps: "Cum functioneaza", projects: "Proiecte", case_study: "Studiu de caz",
  benefits: "Beneficii", testimonials: "Testimoniale", clients: "Clienti",
  gallery: "Galerie foto",
  faq: "Intrebari frecvente", cta: "Apel la actiune", contact: "Contact", footer: "Footer",
};

function MediaThumb({ item, onRemove, idx }) {
  const it = typeof item === "string" ? { url: item, kind: "image" } : item;
  return (
    <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`media-thumb-${idx}`}>
      {it.kind === "video" ? (
        <div className="aspect-square flex items-center justify-center bg-slate-800 text-white">
          <Film className="w-7 h-7" />
        </div>
      ) : it.kind === "pdf" ? (
        <div className="aspect-square flex flex-col items-center justify-center gap-1 p-2 text-slate-600">
          <FileText className="w-7 h-7" />
          <span className="text-[10px] text-center leading-tight truncate w-full">{it.name || "PDF"}</span>
        </div>
      ) : (
        <div className="aspect-square"><img src={imgSrc(it.url)} alt="" className="w-full h-full object-cover" /></div>
      )}
      <button type="button" onClick={onRemove}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`media-remove-${idx}`}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function MultiMediaUpload({ items, onChange, accept, addLabel = "Adauga fisiere", max = 20 }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const list = items || [];
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - list.length;
    if (remaining <= 0) { toast.error(`Maxim ${max} fisiere`); if (ref.current) ref.current.value = ""; return; }
    const chosen = files.slice(0, remaining);
    setBusy(true);
    try {
      const added = [];
      for (const f of chosen) {
        const r = await uploadFile(f);
        added.push({ url: r.url, kind: r.kind, name: r.name || f.name });
      }
      onChange([...list, ...added]);
      toast.success(`${added.length} fisiere incarcate`);
      if (files.length > remaining) toast.error(`Poti avea maxim ${max} fisiere`);
    } catch (err) { toast.error(apiError(err, "Incarcare esuata")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const removeAt = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="space-y-3" data-testid="multi-media-upload">
      {list.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {list.map((it, i) => <MediaThumb key={i} item={it} idx={i} onRemove={() => removeAt(i)} />)}
        </div>
      )}
      <input ref={ref} type="file" accept={accept} multiple className="hidden" onChange={pick} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1"
        data-testid="media-add-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
        {addLabel}
      </button>
    </div>
  );
}

const GALLERY_TABS = [
  { key: "foto", label: "Foto", hint: "Poze (JPG, PNG, WEBP)", accept: "image/*", addLabel: "Adauga poze" },
  { key: "filme", label: "Filme", hint: "Video incarcate direct (MP4, WEBM, MOV, max 60MB)", accept: "video/*", addLabel: "Adauga filme" },
  { key: "certificari", label: "Certificari", hint: "Poze sau PDF", accept: "image/*,application/pdf", addLabel: "Adauga fisiere" },
  { key: "tehnice", label: "Tehnice", hint: "Poze sau PDF", accept: "image/*,application/pdf", addLabel: "Adauga fisiere" },
];

function emptyGalleryTabs() {
  return {
    foto: { enabled: false, items: [] },
    filme: { enabled: false, items: [] },
    certificari: { enabled: false, items: [] },
    tehnice: { enabled: false, items: [] },
  };
}

function readGalleryTabs(data) {
  const base = emptyGalleryTabs();
  const src = data?.tabs;
  if (src) {
    for (const { key } of GALLERY_TABS) {
      const t = src[key];
      if (t) base[key] = { enabled: !!t.enabled, items: Array.isArray(t.items) ? t.items : [] };
    }
    return base;
  }
  if (Array.isArray(data?.images) && data.images.length) {
    base.foto = { enabled: true, items: data.images.map((u) => ({ url: u, kind: "image" })) };
  }
  return base;
}

function GalleryManager({ block, onChange }) {
  const data = block?.data || {};
  const tabs = readGalleryTabs(data);
  const [active, setActive] = useState("foto");
  const commit = (nextTabs) => {
    const nd = { ...data };
    delete nd.images;
    nd.heading = nd.heading || "Galerie";
    nd.subheading = nd.subheading || "Lucrari si produse";
    nd.tabs = nextTabs;
    onChange(nd);
  };
  const setTab = (key, patch) => commit({ ...tabs, [key]: { ...tabs[key], ...patch } });
  const cur = GALLERY_TABS.find((t) => t.key === active);
  const curData = tabs[active];
  return (
    <div className="space-y-4" data-testid="gallery-manager">
      <div className="flex flex-wrap gap-2">
        {GALLERY_TABS.map((t) => {
          const count = tabs[t.key].items.length;
          return (
            <button key={t.key} type="button" onClick={() => setActive(t.key)} data-testid={`gallery-mgr-tab-${t.key}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${active === t.key ? "bg-black text-white border-black" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}{count ? ` (${count})` : ""}{tabs[t.key].enabled ? " ✓" : ""}
            </button>
          );
        })}
      </div>
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid={`gallery-enable-${active}`}>
          <input type="checkbox" checked={curData.enabled} onChange={(e) => setTab(active, { enabled: e.target.checked })} className="w-4 h-4 accent-black" />
          Afiseaza tab-ul "{cur.label}" vizitatorilor
        </label>
        <p className="text-xs text-slate-400">{cur.hint}</p>
        <MultiMediaUpload
          key={active}
          items={curData.items}
          onChange={(items) => setTab(active, { items, enabled: curData.enabled || items.length > 0 })}
          accept={cur.accept}
          addLabel={cur.addLabel}
        />
      </div>
    </div>
  );
}

function setDeep(obj, path, val) {
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[path[path.length - 1]] = val;
  return clone;
}

function FieldEditor({ value, path, onChange }) {
  if (typeof value === "string") {
    const key = path[path.length - 1];
    if (key === "image_url") {
      return <ImageUpload value={value} onChange={(v) => onChange(path, v)} />;
    }
    if (["body", "description", "intro"].includes(key)) {
      return (
        <div>
          <textarea
            data-testid={`fld-${path.join("-")}`}
            maxLength={1000} rows={10}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
            value={value} onChange={(e) => onChange(path, e.target.value.slice(0, 1000))} />
          <div className="text-[11px] text-slate-400 mt-1 text-right" data-testid={`count-${path.join("-")}`}>
            {value.length}/1000
          </div>
        </div>
      );
    }
    const long = value.length > 60;
    return long ? (
      <textarea
        data-testid={`fld-${path.join("-")}`}
        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
        rows={2} value={value} onChange={(e) => onChange(path, e.target.value)} />
    ) : (
      <input
        data-testid={`fld-${path.join("-")}`}
        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
        value={value} onChange={(e) => onChange(path, e.target.value)} />
    );
  }
  if (Array.isArray(value)) {
    const makeEmpty = () => {
      const tmpl = value[0];
      if (tmpl && typeof tmpl === "object" && !Array.isArray(tmpl)) {
        const o = {};
        Object.keys(tmpl).forEach((k) => { o[k] = ""; });
        return o;
      }
      return "";
    };
    return (
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="pl-3 border-l-2 border-emerald-200" data-testid={`fld-item-${path.join("-")}-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Element {i + 1}</span>
              <button type="button" onClick={() => onChange(path, value.filter((_, j) => j !== i))}
                className="text-slate-300 hover:text-red-500" data-testid={`fld-del-${path.join("-")}-${i}`} title="Elimina element">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {React.createElement(FieldEditor, { value: item, path: [...path, i], onChange })}
          </div>
        ))}
        <button type="button" onClick={() => onChange(path, [...value, makeEmpty()])}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1"
          data-testid={`fld-add-${path.join("-")}`}>
          <Plus className="w-3.5 h-3.5" /> Adauga element
        </button>
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([k, v]) => (
          <div key={k}>
            <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-0.5">{k}</div>
            {React.createElement(FieldEditor, { value: v, path: [...path, k], onChange })}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function emptyItem() { return { heading: "", body: "", price: "", image_url: "" }; }

function deriveManualInitial(state) {
  const blocks = state?.blocks || [];
  const theme = state?.theme || {};
  const byType = (t) => (blocks.find((b) => b.type === t) || {}).data || {};
  const hero = byType("hero");
  const cards = byType("cards");
  const contact = byType("contact");
  const items = (cards.items || []).map((it) => ({
    heading: it.heading || "", body: it.body || "", price: it.price || "", image_url: it.image_url || "",
  }));
  return {
    company_name: theme.company_name || hero.heading || "",
    tagline: hero.tagline || "",
    description: hero.body || "",
    products_intro: cards.intro || "",
    primary_color: theme.primary_color || "#ffffff",
    accent_color: theme.accent_color || "#111827",
    hero_image_url: hero.image_url || "",
    gallery_images: [],
    items: items.length ? items : [emptyItem()],
    contact: { phone: contact.phone || "", email: contact.email || "", address: contact.address || "" },
  };
}

function ManualBuilder({ defaults, initial, edit = false, isSuperAdmin = false, onCreated, onCancel }) {
  const [form, setForm] = useState(initial || {
    company_name: defaults?.company_name || "",
    tagline: "",
    description: "",
    products_intro: "",
    primary_color: defaults?.primary_color || "#ffffff",
    accent_color: defaults?.accent_color || "#111827",
    hero_image_url: "",
    gallery_images: [],
    items: [emptyItem()],
    contact: { phone: "", email: "", address: "" },
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, j) => j === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Completeaza numele firmei"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/landing/manual", { ...form, edit });
      toast.success(edit ? "Modificari salvate" : "Pagina generata. Verifica si publica.");
      onCreated(data);
    } catch (err) { toast.error(apiError(err, edit ? "Salvare esuata" : "Generare esuata")); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6 space-y-5" data-testid="manual-builder">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">{edit ? "Editeaza pagina existenta" : "Creare manuala a paginii"}</h3>
        {onCancel && <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-600" data-testid="manual-cancel">Anuleaza</button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nume firma"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Ex: Firma Mea SRL" data-testid="manual-company" /></Field>
        <Field label="Slogan scurt (optional)"><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Ex: Calitate din 2010" data-testid="manual-tagline" /></Field>
      </div>
      <Field label="Descriere firma (activitatea generala a firmei)">
        <textarea data-testid="manual-description" rows={10} maxLength={1000}
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
          value={form.description} onChange={(e) => set("description", e.target.value.slice(0, 1000))}
          placeholder="Ex: Producem mobilier la comanda si oferim servicii de montaj." />
        <div className="text-[11px] text-slate-400 mt-1 text-right" data-testid="manual-description-count">{form.description.length}/1000</div>
      </Field>
      {isSuperAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Culoare principala"><input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200" data-testid="manual-primary" /></Field>
          <Field label="Culoare accent"><input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200" data-testid="manual-accent" /></Field>
        </div>
      )}
      <Field label="Poza principala (optional)">
        <ImageUpload value={form.hero_image_url} onChange={(v) => set("hero_image_url", v)} />
      </Field>

      {!edit && (
        <Field label="Galerie foto (poze multiple, optional)">
          <MultiImageUpload values={form.gallery_images} onChange={(v) => set("gallery_images", v)} />
        </Field>
      )}

      <Field label="Descriere generala a produselor si serviciilor">
        <textarea data-testid="manual-products-intro" rows={10} maxLength={1000}
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
          value={form.products_intro} onChange={(e) => set("products_intro", e.target.value.slice(0, 1000))}
          placeholder="Ex: Oferim o gama variata de ferestre si usi din lemn stratificat." />
        <div className="text-[11px] text-slate-400 mt-1 text-right" data-testid="manual-products-intro-count">{form.products_intro.length}/1000</div>
      </Field>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Servicii / produse</div>
          <button onClick={addItem} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1" data-testid="manual-add-item"><Plus className="w-3.5 h-3.5" /> Adauga</button>
        </div>
        {form.items.map((it, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`manual-item-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Element {i + 1}</span>
              {form.items.length > 1 && <button onClick={() => delItem(i)} className="text-slate-300 hover:text-red-500" data-testid={`manual-del-item-${i}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={it.heading} onChange={(e) => setItem(i, "heading", e.target.value)} placeholder="Titlu (ex: Montaj mobilier)" data-testid={`manual-item-heading-${i}`} />
              <Input value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="Pret (optional)" data-testid={`manual-item-price-${i}`} />
            </div>
            <textarea rows={2} className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 outline-none"
              value={it.body} onChange={(e) => setItem(i, "body", e.target.value)} placeholder="Descriere scurta" data-testid={`manual-item-body-${i}`} />
            <ImageUpload value={it.image_url} onChange={(v) => setItem(i, "image_url", v)} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Telefon"><Input value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} data-testid="manual-phone" /></Field>
        <Field label="Email"><Input value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} data-testid="manual-email" /></Field>
        <Field label="Adresa"><Input value={form.contact.address} onChange={(e) => setContact("address", e.target.value)} data-testid="manual-address" /></Field>
      </div>

      <div className="flex justify-end">
        <Btn onClick={submit} disabled={saving} data-testid="manual-generate-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (edit ? <Save className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)} {edit ? "Salveaza modificarile" : "Genereaza pagina"}
        </Btn>
      </div>
    </Card>
  );
}


function SiteInner() {
  const { activeTenant, isSuperAdmin } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualMode, setManualMode] = useState(null); // null | "create" | "edit"
  const [showPreview, setShowPreview] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [importingUrl, setImportingUrl] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [dirty, setDirty] = useState(false);
  const [leaveProceed, setLeaveProceed] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  useEffect(() => {
    const g = { isDirty: () => dirtyRef.current, open: (proceed) => setLeaveProceed(() => proceed) };
    setLeaveGuard(g);
    return () => clearLeaveGuard(g);
  }, []);

  useEffect(() => {
    const h = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/landing"); setState(data); setDirty(false); }
    finally { setLoading(false); }
  }, [activeTenant]);
  useEffect(() => { load(); }, [load]);

  const doImportUrl = async () => {
    const url = siteUrl.trim();
    if (!url) { toast.error("Scrie adresa site-ului"); return; }
    setImportingUrl(true);
    try {
      const { data } = await api.post("/landing/import-url", { url }, { timeout: 180000 });
      setState((s) => ({ ...data, slug: s?.slug })); setDirty(false);
      toast.success("Site importat. Verifica, editeaza si publica pagina.");
    } catch (err) { toast.error(apiError(err, "Import esuat")); }
    finally { setImportingUrl(false); }
  };

  const onField = (path, val) => { setDirty(true); setState((s) => ({ ...s, blocks: setDeep(s.blocks, path, val) })); };
  const onTheme = (k, v) => { setDirty(true); setState((s) => ({ ...s, theme: { ...s.theme, [k]: v } })); };

  const moveBlock = (from, to) => setState((s) => {
    const blocks = [...(s.blocks || [])];
    if (from < 0 || from >= blocks.length || to < 0 || to >= blocks.length) return s;
    const [m] = blocks.splice(from, 1);
    blocks.splice(to, 0, m);
    setDirty(true);
    return { ...s, blocks };
  });

  const galleryBlock = () => (state?.blocks || []).find((x) => x.type === "gallery") || null;
  const setGalleryData = (nd) => setState((s) => {
    setDirty(true);
    const blocks = [...(s.blocks || [])];
    const i = blocks.findIndex((x) => x.type === "gallery");
    if (i >= 0) {
      blocks[i] = { ...blocks[i], data: nd };
    } else {
      const gb = { type: "gallery", data: nd };
      const ci = blocks.findIndex((x) => x.type === "contact");
      if (ci >= 0) blocks.splice(ci, 0, gb); else blocks.push(gb);
    }
    return { ...s, blocks };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/landing", { blocks: state.blocks, theme: state.theme, source: "manual" });
      setState((s) => ({ ...data, slug: s?.slug })); setDirty(false); setSavedAt(Date.now());
      toast.success("Modificari salvate");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const autoSave = useCallback(async () => {
    if (!(state && state.blocks && state.blocks.length)) return;
    setAutoSaving(true);
    try {
      const { data } = await api.put("/landing", { blocks: state.blocks, theme: state.theme, source: "auto" });
      setState((s) => ({ ...data, slug: s?.slug })); setDirty(false); setSavedAt(Date.now());
    } catch (err) { /* silent, va reincerca la urmatoarea modificare */ }
    finally { setAutoSaving(false); }
  }, [state]);

  const autoInterval = Number(state?.autosave_interval) || 30;

  useEffect(() => {
    if (!dirty) return;
    if (!(state && state.blocks && state.blocks.length)) return;
    const t = setTimeout(() => { autoSave(); }, autoInterval * 1000);
    return () => clearTimeout(t);
  }, [state, dirty, autoSave, autoInterval]);

  const changeInterval = async (val) => {
    const iv = parseInt(val, 10) || 30;
    setState((s) => ({ ...s, autosave_interval: iv }));
    try { await api.put("/landing", { autosave_interval: iv }); toast.success("Interval salvat"); }
    catch (err) { toast.error(apiError(err, "Nu am putut salva intervalul")); }
  };

  const openVersions = async () => {
    setShowVersions(true); setVersionsLoading(true);
    try { const { data } = await api.get("/landing/versions"); setVersions(data.versions || []); }
    catch (err) { toast.error(apiError(err, "Nu am putut incarca istoricul")); }
    finally { setVersionsLoading(false); }
  };

  const restoreVersion = async (id) => {
    setRestoringId(id);
    try {
      const { data } = await api.post(`/landing/versions/${id}/restore`);
      setState((s) => ({ ...data, slug: s?.slug })); setDirty(false); setSavedAt(Date.now());
      setShowVersions(false);
      toast.success("Versiune restaurata");
    } catch (err) { toast.error(apiError(err, "Restaurare esuata")); }
    finally { setRestoringId(null); }
  };

  const publish = async () => {
    try { await api.post("/landing/publish"); await load(); toast.success("Pagina publicata"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const unpublish = async () => {
    try { await api.post("/landing/unpublish"); await load(); toast.success("Pagina retrasa"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const removePage = async () => {
    if (!window.confirm("Stergi aceasta pagina? Va fi scoasa de pe site si arhivata. Poti crea sau importa una noua dupa.")) return;
    try {
      await api.delete("/landing");
      setManualMode(null);
      setDirty(false); dirtyRef.current = false;
      await load();
      toast.success("Pagina stearsa");
    } catch (err) { toast.error(apiError(err)); }
  };

  const leaveSaveAndGo = async () => {
    setLeaving(true);
    try {
      const { data } = await api.put("/landing", { blocks: state.blocks, theme: state.theme });
      setState((s) => ({ ...data, slug: s?.slug }));
      setDirty(false);
      toast.success("Modificari salvate");
      const p = leaveProceed; setLeaveProceed(null);
      if (p) p();
    } catch (err) { toast.error(apiError(err, "Salvare esuata")); }
    finally { setLeaving(false); }
  };
  const leaveDiscardAndGo = () => {
    setDirty(false); dirtyRef.current = false;
    const p = leaveProceed; setLeaveProceed(null);
    if (p) p();
  };
  const leaveStay = () => setLeaveProceed(null);

  if (loading) return <Spinner />;

  const hasPage = state && state.blocks && state.blocks.length > 0;
  const published = state?.status === "published";
  const publicUrl = state?.slug ? `${BASE}/site/${state.slug}` : null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><PencilRuler className="w-5 h-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900">Creare pagina</h3>
              <p className="text-sm text-slate-500 mt-0.5">Creeaza pagina manual: adauga poze proprii si descrierea activitatii.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {hasPage && (
              <Btn onClick={() => setManualMode((m) => (m === "edit" ? null : "edit"))} data-testid="edit-page-toggle">
                <PencilRuler className="w-4 h-4" /> Editeaza pagina
              </Btn>
            )}
            <Btn variant="outline" onClick={() => {
              if (hasPage && !window.confirm("Creezi o pagina noua de la zero? Continutul actual va fi inlocuit.")) return;
              setManualMode((m) => (m === "create" ? null : "create"));
            }} data-testid="manual-builder-toggle">
              <Sparkles className="w-4 h-4" /> {hasPage ? "Creare pagina noua" : "Creare manuala"}
            </Btn>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-slate-100" data-testid="import-url-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center"><Globe className="w-5 h-5" /></div>
            <div>
              <h3 className="font-semibold text-slate-900">Importa de pe site propriu</h3>
              <p className="text-sm text-slate-500 mt-0.5">Ai deja un site? Scrie adresa lui si generam automat pagina din textul si pozele de acolo.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="ex: www.firma-mea.ro" data-testid="import-url-input" />
            </div>
            <Btn onClick={doImportUrl} disabled={importingUrl} data-testid="import-url-btn">
              {importingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              {importingUrl ? "Se importa..." : "Importa site"}
            </Btn>
          </div>
          {importingUrl && <p className="text-xs text-slate-400 mt-2">Poate dura pana la un minut. Citim site-ul, generam textul si preluam pozele.</p>}
        </div>
      </Card>

      {manualMode && (
        <ManualBuilder
          key={manualMode}
          edit={manualMode === "edit"}
          isSuperAdmin={isSuperAdmin}
          defaults={state?.theme}
          initial={manualMode === "edit" ? deriveManualInitial(state) : null}
          onCreated={(data) => { setState((s) => ({ ...data, slug: s?.slug })); setDirty(false); setManualMode(null); }}
          onCancel={() => setManualMode(null)}
        />
      )}

      {!hasPage ? (
        <Card>
          <Empty icon={Globe} title="Nicio pagina inca"
            hint="Foloseste Creare manuala pentru a incarca poze proprii si descrierea activitatii." />
        </Card>
      ) : (
        <>
          <Card className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="landing-status">
                {published ? "Publicat" : "Ciorna"}
              </span>
              {published && publicUrl && (
                <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-emerald-600 hover:underline flex items-center gap-1" data-testid="public-site-link">
                  <ExternalLink className="w-3.5 h-3.5" /> Vezi site public
                </a>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {autoSaving ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 flex items-center gap-1" data-testid="autosave-indicator">
                  <Loader2 className="w-3 h-3 animate-spin" /> Se salveaza automat...
                </span>
              ) : dirty ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="unsaved-indicator">
                  Modificari nesalvate
                </span>
              ) : savedAt ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="saved-indicator">
                  Salvat automat
                </span>
              ) : null}
              <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="autosave-interval-wrap">
                <Clock className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Salvare la</span>
                <select value={autoInterval} onChange={(e) => changeInterval(e.target.value)}
                  className="text-xs font-semibold rounded-lg border border-slate-200 px-2 py-1 outline-none focus:border-emerald-500"
                  data-testid="autosave-interval-select">
                  <option value={5}>5 sec</option>
                  <option value={15}>15 sec</option>
                  <option value={30}>30 sec</option>
                  <option value={60}>60 sec</option>
                </select>
              </div>
              <Btn variant="outline" onClick={openVersions} data-testid="versions-button"><History className="w-4 h-4" /> Istoric</Btn>
              <Btn variant="outline" onClick={() => setShowPreview(true)} data-testid="preview-button"><Eye className="w-4 h-4" /> Previzualizare</Btn>
              <Btn variant={dirty ? "primary" : "outline"} onClick={save} disabled={saving} data-testid="save-landing-button"><Save className="w-4 h-4" /> Salveaza</Btn>
              {published
                ? <Btn variant="outline" onClick={unpublish} data-testid="unpublish-button"><EyeOff className="w-4 h-4" /> Retrage</Btn>
                : <Btn onClick={publish} data-testid="publish-button"><Rocket className="w-4 h-4" /> Publica</Btn>}
              <Btn variant="danger" onClick={removePage} data-testid="delete-page-button"><Trash2 className="w-4 h-4" /> Sterge pagina</Btn>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4">{isSuperAdmin ? "Aspect (culori)" : "Nume firma"}</h3>
            <div className={`grid grid-cols-1 gap-4 ${isSuperAdmin ? "sm:grid-cols-3" : ""}`}>
              <Field label="Nume firma"><Input value={state.theme?.company_name || ""} onChange={(e) => onTheme("company_name", e.target.value)} data-testid="theme-company" /></Field>
              {isSuperAdmin && <Field label="Culoare principala"><input type="color" value={state.theme?.primary_color || "#ffffff"} onChange={(e) => onTheme("primary_color", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200" data-testid="theme-primary" /></Field>}
              {isSuperAdmin && <Field label="Culoare accent"><input type="color" value={state.theme?.accent_color || "#111827"} onChange={(e) => onTheme("accent_color", e.target.value)} className="w-full h-10 rounded-lg border border-slate-200" data-testid="theme-accent" /></Field>}
            </div>
            {isSuperAdmin && <p className="text-xs text-slate-400 mt-3">Implicit pagina este alba cu text negru. Alege culori doar daca vrei sa personalizezi.</p>}
          </Card>

          <Card className="p-6" data-testid="gallery-card">
            <h3 className="font-semibold text-slate-900 mb-1">Galerie</h3>
            <p className="text-sm text-slate-500 mb-4">Alege ce vede vizitatorul: Foto, Filme, Certificari, Tehnice. Bifeaza tab-urile dorite si incarca fisiere. Salveaza dupa.</p>
            <GalleryManager block={galleryBlock() || { data: {} }} onChange={setGalleryData} />
          </Card>

          <div className="space-y-4" data-testid="blocks-editor">
            <p className="text-xs text-slate-400">Trage blocul de manerul din stanga ca sa il muti, sau foloseste sagetile. Salveaza dupa.</p>
            {state.blocks.map((b, bi) => (
              b.type === "gallery" ? null : (
              <Card
                key={bi}
                className={`p-6 transition-opacity ${dragIdx === bi ? "opacity-40" : ""}`}
                data-testid={`block-editor-${b.type}-${bi}`}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== bi) moveBlock(dragIdx, bi); setDragIdx(null); }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span
                    draggable
                    onDragStart={() => setDragIdx(bi)}
                    onDragEnd={() => setDragIdx(null)}
                    className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 -ml-1"
                    data-testid={`block-drag-${bi}`}
                    title="Trage ca sa reordonezi"
                  >
                    <GripVertical className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 flex-1">{LABELS[b.type] || b.type}</span>
                  <button type="button" onClick={() => moveBlock(bi, bi - 1)} disabled={bi === 0}
                    className="text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-300"
                    data-testid={`block-up-${bi}`} title="Muta sus"><ChevronUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => moveBlock(bi, bi + 1)} disabled={bi === state.blocks.length - 1}
                    className="text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-300"
                    data-testid={`block-down-${bi}`} title="Muta jos"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <FieldEditor value={b.data || {}} path={[bi, "data"]} onChange={onField} />
              </Card>
              )
            ))}
          </div>
        </>
      )}

      {showPreview && hasPage && (
        <div className="fixed inset-0 z-50 bg-white overflow-auto" data-testid="site-preview" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
          <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 bg-white text-black border-b border-gray-300">
            <span className="text-sm font-medium flex items-center gap-2"><Eye className="w-4 h-4" /> Previzualizare pagina (asa o vad vizitatorii)</span>
            <button onClick={() => setShowPreview(false)} className="p-1.5 rounded-md hover:bg-gray-100" data-testid="close-preview-button"><X className="w-5 h-5" /></button>
          </div>
          <LightboxProvider>
            {(state.blocks || []).map((b, i) => (
              <BlockRenderer key={i} block={b} theme={state.theme || {}} />
            ))}
          </LightboxProvider>
        </div>
      )}

      {showVersions && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="versions-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2"><History className="w-5 h-5" /> Istoric versiuni</h3>
              <button onClick={() => setShowVersions(false)} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="versions-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500">Se pastreaza ultimele 20 de versiuni. Poti reveni la o versiune anterioara. Versiunea curenta este salvata automat inainte de restaurare.</p>
            <div className="flex-1 overflow-auto -mx-1 px-1">
              {versionsLoading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : versions.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-400" data-testid="versions-empty">Nicio versiune anterioara inca. Se creeaza pe masura ce editezi.</div>
              ) : (
                <div className="space-y-2" data-testid="versions-list">
                  {versions.map((v) => {
                    const label = v.source === "manual" ? "Salvare manuala" : v.source === "restore-point" ? "Inainte de restaurare" : "Salvare automata";
                    const dt = v.created_at ? new Date(v.created_at).toLocaleString("ro-RO") : "";
                    return (
                      <div key={v.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3" data-testid={`version-item-${v.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800">{dt}</div>
                          <div className="text-xs text-slate-400">{label} · {v.blocks_count} blocuri{v.company_name ? ` · ${v.company_name}` : ""}</div>
                        </div>
                        <Btn variant="outline" onClick={() => restoreVersion(v.id)} disabled={restoringId === v.id} data-testid={`version-restore-${v.id}`}>
                          {restoringId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restaureaza
                        </Btn>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {leaveProceed && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="site-leave-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-semibold text-slate-900 text-lg">Modificari nesalvate</h3>
            <p className="text-sm text-slate-600">Ai modificari nesalvate pe pagina. Vrei sa le salvezi inainte de a iesi?</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Btn variant="outline" onClick={leaveStay} disabled={leaving} data-testid="site-leave-stay">Ramai pe pagina</Btn>
              <Btn variant="outline" onClick={leaveDiscardAndGo} disabled={leaving} data-testid="site-leave-discard">Iesi fara salvare</Btn>
              <Btn onClick={leaveSaveAndGo} disabled={leaving} data-testid="site-leave-save">
                {leaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salveaza si iesi
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Site() {
  return (
    <div>
      <PageHeader title="Site public" subtitle="Creeaza si publica pagina de prezentare a firmei." />
      <p
        data-testid="site-b2b-notice"
        style={{ fontFamily: "Arial, sans-serif", fontSize: "15px", fontWeight: "bold", color: "#dc2626" }}
        className="mb-4"
      >
        Acest site pe care il veti crea, va ajuta sa obtineti clienti B2B
      </p>
      <AjutorButton file="ajutor_site_public.pdf" />
      <RequireTenant><SiteInner /></RequireTenant>
    </div>
  );
}


